import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../shared/contexts/AuthContext';
import axiosInstance from '../../../api/axiosInstance';
import './RegisterScreen.css';

type Step = 'email' | 'code' | 'password';

const ForgotPasswordScreen: React.FC = () => {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendInfo, setResendInfo] = useState<string | null>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);

  const { loginWithTokens } = useAuth();
  const navigate = useNavigate();

  const requestCode = async () => {
    await axiosInstance.post('/auth/password-reset/request', { email });
  };

  const handleSubmitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await requestCode();
      setStep('code');
      setCode('');
      setTimeout(() => codeInputRef.current?.focus(), 100);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Произошла ошибка. Попробуйте ещё раз');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResendInfo(null);
    setIsLoading(true);
    try {
      const res = await axiosInstance.post<{ success: boolean; resetToken: string }>(
        '/auth/password-reset/verify',
        { email, code }
      );
      setResetToken(res.data.resetToken);
      setStep('password');
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Неверный код');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== password2) {
      setError('Пароли не совпадают');
      return;
    }
    if (password.length < 6) {
      setError('Пароль должен содержать минимум 6 символов');
      return;
    }

    setIsLoading(true);
    try {
      const res = await axiosInstance.post<{
        success: boolean;
        accessToken: string;
        refreshToken: string;
        user: { id: number; name: string };
      }>('/auth/password-reset/confirm', { email, resetToken, newPassword: password });

      loginWithTokens(res.data.accessToken, res.data.refreshToken, res.data.user);
      navigate('/dashboard-new', { replace: true });
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Не удалось сменить пароль');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setError(null);
    setResendInfo(null);
    setIsLoading(true);
    try {
      await requestCode();
      setResendInfo('Новый код отправлен');
      setCode('');
      codeInputRef.current?.focus();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Не удалось отправить код');
    } finally {
      setIsLoading(false);
    }
  };

  const titles: Record<Step, string> = {
    email: 'Восстановление пароля',
    code: 'Код из письма',
    password: 'Новый пароль',
  };

  return (
    <div className="register-screen">
      <div className="register-container">
        <div className="register-header">
          <h1 className="register-title">{titles[step]}</h1>
          <p className="register-subtitle">Ryaskov.pro — Система управления ремонтом</p>
        </div>

        {step === 'email' && (
          <form onSubmit={handleSubmitEmail} className="register-form">
            {error && (
              <div className="error-message">
                <i className="ri-error-warning-line" />
                {error}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email" className="form-label">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="form-input"
                required
                disabled={isLoading}
                autoComplete="email"
              />
              <small className="form-help">
                Укажите email, с которым вы регистрировались — мы отправим на него код
              </small>
            </div>

            <button
              type="submit"
              className="register-button"
              disabled={isLoading || !email}
            >
              {isLoading ? (
                <>
                  <i className="ri-loader-4-line ri-spin" />
                  Отправляем код…
                </>
              ) : (
                <>
                  <i className="ri-mail-send-line" />
                  Получить код
                </>
              )}
            </button>
          </form>
        )}

        {step === 'code' && (
          <form onSubmit={handleSubmitCode} className="register-form">
            <p className="code-sent-text">
              Если <span className="success-email">{email}</span> зарегистрирован,
              мы отправили на него 6-значный код
            </p>

            {error && (
              <div className="error-message">
                <i className="ri-error-warning-line" />
                {error}
              </div>
            )}
            {resendInfo && (
              <div className="info-message">
                <i className="ri-mail-check-line" />
                {resendInfo}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="code" className="form-label">Код из письма</label>
              <input
                id="code"
                ref={codeInputRef}
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="form-input code-input"
                required
                disabled={isLoading}
                autoComplete="one-time-code"
              />
              <small className="form-help">
                Код действителен 15 минут. Не пришло? Проверьте «Спам»
              </small>
            </div>

            <button
              type="submit"
              className="register-button"
              disabled={isLoading || code.length !== 6}
            >
              {isLoading ? (
                <>
                  <i className="ri-loader-4-line ri-spin" />
                  Проверяем…
                </>
              ) : (
                <>
                  <i className="ri-checkbox-circle-line" />
                  Подтвердить
                </>
              )}
            </button>

            <button
              type="button"
              className="register-back-button"
              onClick={handleResend}
              disabled={isLoading}
            >
              <i className="ri-refresh-line" />
              Отправить код ещё раз
            </button>

            <button
              type="button"
              className="register-back-button"
              onClick={() => { setStep('email'); setError(null); setResendInfo(null); }}
              disabled={isLoading}
            >
              <i className="ri-arrow-left-line" />
              Изменить email
            </button>
          </form>
        )}

        {step === 'password' && (
          <form onSubmit={handleSubmitPassword} className="register-form">
            {error && (
              <div className="error-message">
                <i className="ri-error-warning-line" />
                {error}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="password" className="form-label">Новый пароль</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Минимум 6 символов"
                className="form-input"
                required
                disabled={isLoading}
                autoComplete="new-password"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password2" className="form-label">Повторите пароль</label>
              <input
                id="password2"
                type="password"
                value={password2}
                onChange={e => setPassword2(e.target.value)}
                placeholder="Повторите пароль"
                className="form-input"
                required
                disabled={isLoading}
                autoComplete="new-password"
              />
            </div>

            <button
              type="submit"
              className="register-button"
              disabled={isLoading || !password || !password2}
            >
              {isLoading ? (
                <>
                  <i className="ri-loader-4-line ri-spin" />
                  Сохраняем…
                </>
              ) : (
                <>
                  <i className="ri-lock-password-line" />
                  Сменить пароль и войти
                </>
              )}
            </button>
          </form>
        )}

        {step === 'email' && (
          <div className="register-footer">
            <p className="login-link-text">
              Вспомнили пароль?{' '}
              <button className="login-link" onClick={() => navigate('/login')}>
                Войти
              </button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordScreen;
